import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { supabase } from "@/lib/supabaseClient";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=guest, obj=authed (with roles/permissions)

  // Resolve the app-level profile (roles/permissions) from the backend using the Supabase token.
  // Returns the user object on success, or false if the session isn't fully authenticated (e.g. MFA pending).
  const loadMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(false);
      return false;
    }
  }, []);

  useEffect(() => {
    let active = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      if (data.session) loadMe(); else setUser(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) loadMe(); else setUser(false);
    });
    return () => { active = false; sub.subscription.unsubscribe(); };
  }, [loadMe]);

  const logout = useCallback(async () => {
    try { await supabase.auth.signOut(); } catch (e) { /* ignore */ }
    setUser(false);
  }, []);

  const value = useMemo(() => ({ user, loadMe, logout }), [user, loadMe, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
